import { sessionQueryVersionAtom } from '@/atoms/sessionAtoms';
import { selectedReviewMemberAtom } from '@/atoms/teamAtoms';
import { useCombinedQueriesTeamGeneralInfo } from '@/hooks/query/useCombinedQueriesTeamGeneralInfo';
import {
  invalidateSessionsQuery,
  invalidateTeamMemberSessionsQuery,
} from '@/hooks/query/useQueryCombinedSessions';
import { useIsReviewingOtherUser } from '@/hooks/query/useQueryUser';
import { trackSessionStatusUpdated } from '@/models/analytics/features/trackSessionLifecycleAnalytics';
import { updateSessionById } from '@/pages/api/scribeAPI';
import { updateSessionForOtherUser } from '@/pages/api/scribeAPIV2';
import { logger } from '@/utils/logger/logger';
import { toast } from '@heidi/design-system/toast';
import { type SessionStatus } from '@heidi/schema/ml-scribe';
import type { SessionListResponse } from '@heidi/session/contracts';
import {
  ASSISTANT_VIEW_SELECT_ID,
  GET_SESSION_BY_ID,
  GET_SESSIONS_LIST,
  GET_TEAM_MEMBER_SESSION_BY_ID,
  type SessionResponse,
  type SessionsResponse,
} from '@heidi/session/contracts';
import { invalidateSessionById, invalidateSessionStatusCount } from '@heidi/session/data';
import { type InfiniteData, useMutation, useQueryClient } from '@tanstack/react-query';
import produce from 'immer';
import { useAtomValue } from 'jotai';
import { GET_SESSIONS } from '../query/useQuerySessions';
import { GET_INFINITE_SESSIONS } from '../query/useQuerySessionsV2';
import { GET_INFINITE_SESSIONS_WITH_PAGINATION } from '../query/useQuerySessionsWithPagination';

type UpdateSessionStatusInput = {
  sessionId: string;
  sessionStatus: SessionStatus;
  /** When changing status from Action Center for a teammate's session, pass teamId + memberId so the v2 team-member endpoint is used (avoids 403 from v1 owner-only check). */
  teamId?: string;
  memberId?: string;
};

export const useMutateUpdateSessionStatus = () => {
  const sessionQueryVersion = useAtomValue(sessionQueryVersionAtom);
  const queryClient = useQueryClient();
  const isReviewingOtherUser = useIsReviewingOtherUser();
  const selectedReviewUser = useAtomValue(selectedReviewMemberAtom);
  const { teamDetails } = useCombinedQueriesTeamGeneralInfo();
  const fallbackTeamMemberContext =
    isReviewingOtherUser &&
    teamDetails?.team.id &&
    selectedReviewUser &&
    selectedReviewUser !== ASSISTANT_VIEW_SELECT_ID
      ? {
          teamId: teamDetails.team.id,
          memberId: selectedReviewUser,
        }
      : null;

  const resolveTeamMemberContext = ({
    teamId,
    memberId,
  }: Pick<UpdateSessionStatusInput, 'teamId' | 'memberId'>) => {
    if (teamId && memberId) {
      return { teamId, memberId };
    }
    return fallbackTeamMemberContext;
  };

  return useMutation({
    mutationFn: async ({
      sessionId,
      sessionStatus,
      teamId: inputTeamId,
      memberId: inputMemberId,
    }: UpdateSessionStatusInput) => {
      const teamMemberContext = resolveTeamMemberContext({
        teamId: inputTeamId,
        memberId: inputMemberId,
      });
      if (teamMemberContext) {
        return await updateSessionForOtherUser({
          teamId: teamMemberContext.teamId,
          memberId: teamMemberContext.memberId,
          sessionId,
          input: {
            session_status: sessionStatus,
          },
        });
      }
      return await updateSessionById(sessionId, {
        session_status: sessionStatus,
      });
    },

    onMutate: ({ sessionId, sessionStatus, teamId, memberId }) => {
      const teamMemberContext = resolveTeamMemberContext({ teamId, memberId });

      // New session-list (`useQuerySessionsList`) is a separate cache that
      // coexists with the legacy v1/v2/v3 queries below — patch it
      // unconditionally so the row chip updates immediately when the user
      // picks a new status from the inline `DataTableEnumCell`.
      void queryClient.setQueriesData<InfiniteData<SessionListResponse>>(
        { queryKey: [GET_SESSIONS_LIST] },
        (oldData) => {
          if (!oldData) return undefined;
          return produce(oldData, (draft) => {
            const session = draft.pages
              .flatMap((page) => page.sessions)
              .find((s) => s.session_id === sessionId);
            if (session) {
              session.session_status = sessionStatus;
            }
          });
        }
      );

      switch (sessionQueryVersion) {
        case 'v3':
        case 'v2':
          void queryClient.setQueriesData<InfiniteData<SessionsResponse>>(
            {
              queryKey: [
                sessionQueryVersion === 'v3'
                  ? GET_INFINITE_SESSIONS_WITH_PAGINATION
                  : GET_INFINITE_SESSIONS,
              ],
            },
            (oldData: InfiniteData<SessionsResponse> | undefined) => {
              if (!oldData) {
                return undefined;
              }
              return produce(oldData, (draft) => {
                const session = draft.pages
                  .flatMap((page) => page.sessions)
                  .find((session) => session.session_id === sessionId);
                if (session) {
                  session.session_status = sessionStatus;
                }
              });
            }
          );
          break;
        default:
          void queryClient.setQueryData<SessionsResponse>([GET_SESSIONS], (oldData) => {
            if (!oldData) {
              return undefined;
            }
            return produce(oldData, (draft) => {
              const session = draft.sessions.find(
                (session) => session.session_id === sessionId
              );
              if (session) {
                session.session_status = sessionStatus;
              }
            });
          });
      }

      const teamMemberSessionQueryKey = teamMemberContext
        ? [
            GET_TEAM_MEMBER_SESSION_BY_ID,
            { teamId: teamMemberContext.teamId },
            { memberId: teamMemberContext.memberId },
            { sessionId },
          ]
        : null;

      const applyStatusPatch = (oldData: SessionResponse | undefined) => {
        if (!oldData) return undefined;
        return produce(oldData, (draft) => {
          draft.session_status = sessionStatus;
        });
      };

      // Always patch GET_SESSION_BY_ID so the open session page updates
      // immediately regardless of which endpoint the mutation uses (the session
      // page reads from this key even when the write goes to the team-member
      // v2 endpoint via `teamMemberContext`).
      void queryClient.setQueryData<SessionResponse>(
        [GET_SESSION_BY_ID, { sessionId }],
        applyStatusPatch
      );

      if (teamMemberSessionQueryKey) {
        void queryClient.setQueryData<SessionResponse>(
          teamMemberSessionQueryKey,
          applyStatusPatch
        );
      }
    },

    onSuccess: (_, { sessionId, sessionStatus, teamId, memberId }) => {
      const teamMemberContext = resolveTeamMemberContext({ teamId, memberId });
      if (teamMemberContext) {
        void invalidateTeamMemberSessionsQuery(
          teamMemberContext.teamId,
          teamMemberContext.memberId
        );
        // The optimistic patch in `onMutate` updates the session-detail cache so
        // the open session page reflects the new status immediately. Invalidate
        // the authoritative key after the write so it re-fetches clean data —
        // without this the session page stays on the stale pre-mutation status.
        invalidateSessionById(sessionId);
      }
      // Invalidate session status counts since status changed
      void invalidateSessionStatusCount();

      // PRODA-102: emit `session_updated` for the dbt
      // `snowplow_session_updated` warehouse model. The helper enforces
      // the Snowplow-only routing so call sites here stay focused on
      // cache + mutation concerns.
      trackSessionStatusUpdated({ sessionId, sessionStatus });
    },

    onError: (error, { sessionId, teamId, memberId }) => {
      const teamMemberContext = resolveTeamMemberContext({ teamId, memberId });
      if (teamMemberContext) {
        void invalidateTeamMemberSessionsQuery(
          teamMemberContext.teamId,
          teamMemberContext.memberId
        );
        void queryClient.invalidateQueries({
          queryKey: [
            GET_TEAM_MEMBER_SESSION_BY_ID,
            { teamId: teamMemberContext.teamId },
            { memberId: teamMemberContext.memberId },
            sessionId,
          ],
        });
        // The team-member branch doesn't go through `invalidateSessionsQuery`,
        // but `onMutate` patches `GET_SESSIONS_LIST` unconditionally — roll it
        // back here so the new session-list row chip doesn't stay stale until
        // the next 30s refetch when a team-member-context mutation fails.
        void queryClient.invalidateQueries({ queryKey: [GET_SESSIONS_LIST] });
        // Roll back the GET_SESSION_BY_ID optimistic patch added in `onMutate`
        // (patched unconditionally so the session page always sees the update).
        invalidateSessionById(sessionId);
      } else {
        void invalidateSessionsQuery();
        invalidateSessionById(sessionId);
      }
      logger.error('Failed to update session status:', error);
      toast({
        tone: 'destructive',
        title: 'Failed to update session status',
      });
    },
  });
};
